import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@ngneat/transloco';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { filter, switchMap } from 'rxjs/operators';
import { OneClickQRESet } from 'src/app/core/models/one-click-qre-set.model';
import {
  OneClickQRESetError,
  OneClickQRESetService
} from 'src/app/core/services/one-click-qre-set.service';
import { ConfirmModalService } from 'src/app/shared/confirm-modal/confirm-modal.service';
import {
  OneClickQRESetEditorDialogComponent,
  OneClickQRESetEditorDialogResult
} from 'src/app/shared/one-click-qre-set-editor-dialog/one-click-qre-set-editor-dialog.component';

export interface OneClickQRESetListItemView {
  id: string;
  name: string;
  itemSummary: string;
  canMutate: boolean;
  source: OneClickQRESet;
}

@Component({
  selector: 'csi-one-click-qre-set-setup',
  templateUrl: './one-click-qre-set-setup.component.html',
  styleUrls: ['./one-click-qre-set-setup.component.css']
})
export class OneClickQRESetSetupComponent implements OnInit, OnDestroy {
  public setViews: OneClickQRESetListItemView[] = [];
  public canCreate = true;
  public canManageSets = false;
  public isEmpty = true;

  constructor(
    private oneClickQRESetService: OneClickQRESetService,
    private matDialog: MatDialog,
    private confirmModalService: ConfirmModalService,
    private snackBar: MatSnackBar,
    private translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.refreshSets();
  }

  ngOnDestroy() {}

  public trackBySetId(_index: number, setView: OneClickQRESetListItemView): string {
    return setView.id;
  }

  public addSet() {
    if (!this.canManageSets) {
      return;
    }
    if (!this.canCreate) {
      this.snackBar.open(
        this.translocoService.translate('Maximum of 5 templates allowed'),
        '',
        { duration: 4000 }
      );
      return;
    }
    OneClickQRESetEditorDialogComponent.open(this.matDialog)
      .afterClosed()
      .pipe(
        filter((result: OneClickQRESetEditorDialogResult) => !!result),
        switchMap((result: OneClickQRESetEditorDialogResult) =>
          this.oneClickQRESetService.createSet(result.name, result.items, true)
        ),
        untilDestroyed(this)
      )
      .subscribe(
        () => {
          this.refreshSets();
          this.snackBar.open(this.translocoService.translate('Template saved'), '', {
            duration: 3000
          });
        },
        (error: OneClickQRESetError) => {
          this.showError(error.errorKey || 'Unable to save template');
        }
      );
  }

  public editSet(setView: OneClickQRESetListItemView) {
    if (!setView.canMutate) {
      return;
    }
    OneClickQRESetEditorDialogComponent.open(this.matDialog, { set: setView.source })
      .afterClosed()
      .pipe(
        filter((result: OneClickQRESetEditorDialogResult) => !!result),
        switchMap((result: OneClickQRESetEditorDialogResult) =>
          this.oneClickQRESetService.updateSet(
            setView.id,
            { name: result.name, items: result.items },
            true
          )
        ),
        untilDestroyed(this)
      )
      .subscribe(
        () => {
          this.refreshSets();
          this.snackBar.open(this.translocoService.translate('Template saved'), '', {
            duration: 3000
          });
        },
        (error: OneClickQRESetError) => {
          this.showError(error.errorKey || 'Unable to save template');
        }
      );
  }

  public deleteSet(setView: OneClickQRESetListItemView) {
    if (!setView.canMutate) {
      return;
    }
    this.confirmModalService
      .show(
        this.translocoService.translate('Delete Template'),
        this.translocoService.translate('Delete this template?'),
        this.translocoService.translate('Delete'),
        this.translocoService.translate('Cancel')
      )
      .pipe(
        filter(confirmed => !!confirmed),
        switchMap(() => this.oneClickQRESetService.deleteSet(setView.id)),
        untilDestroyed(this)
      )
      .subscribe(
        () => {
          this.refreshSets();
          this.snackBar.open(this.translocoService.translate('Template deleted'), '', {
            duration: 3000
          });
        },
        (error: OneClickQRESetError) => {
          this.showError(error.errorKey || 'Unable to delete template');
        }
      );
  }

  private refreshSets() {
    const sets = this.oneClickQRESetService.getSets();
    this.setViews = sets.map(set => ({
      id: set.id,
      name: set.name,
      itemSummary: set.items.map(item => item.label).join(', '),
      canMutate: this.oneClickQRESetService.canMutateSet(set),
      source: set
    }));
    this.canManageSets = this.oneClickQRESetService.canMutateAllSets();
    this.canCreate = this.oneClickQRESetService.canCreateSet();
    this.isEmpty = this.setViews.length === 0;
  }

  private showError(errorKey: string) {
    this.snackBar.open(this.translocoService.translate(errorKey), '', { duration: 4000 });
  }
}
